import { Observable ,  ReplaySubject, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import {Organization, User, UserUpdateModel, UserWriteModel} from '../models/User';
import { CacheService } from './cache.service';
import { environment } from '../../environments/environment';
import { UserSetting } from '../models/UserSetting';
import { ControlantAPIError, ControlantAPIErrorType } from '../models/ControlantAPIError';
import { catchError, map, mapTo, mergeMap, tap } from 'rxjs/operators';
import {HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';

export interface ChangePassword {
    oldPassword: string;
    newPassword: string;
}

export type TemperatureUnit = '°C' | '°F';
export interface UserSettings {
    temperatureUnit: TemperatureUnit;
    timezone: string;
}

@Injectable()
export class UserService {

    get currentUserSettings(): UserSettings {
        return this._currentUserSettings;
    }

    private _currentUserSettings: UserSettings;
    customerUsers: Array<User>;

    _customerUsers$: ReplaySubject<Array<User>> = new ReplaySubject(1);
    /*private _currentUserTimezone$: ReplaySubject<UserSetting> = new ReplaySubject(1);
    private _currentUserTemperatureUnit$: ReplaySubject<UserSetting> = new ReplaySubject(1);
*/

    set currentUser(user: User) {
        this._currentUser = user;

        if (user === null) {

            this._currentUserSettings = null;

        } else {

            if (user.accessLevel === 'ADMIN') {
                this.adminMode = window.sessionStorage.getItem('adminMode') === 'true';
            }

            this._currentUserSettings = {
                timezone: user.activeSettings.find((setting) => setting.key === 'TIMEZONE').value,
                temperatureUnit: user.activeSettings.find((setting) => setting.key === 'UNIT_DE').value as TemperatureUnit
            };
        }

    }

    get currentUser(): User {
        return this._currentUser;
    }

    private _currentUser: User;
    private adminMode: boolean = false;

    constructor(
        private httpService: HttpService,
        private cacheService: CacheService
    ) {
        this.customerUsers$.subscribe(
            users => {
                this.customerUsers = users;
            }
        );
    }

    private fetchCurrentUser(): Observable<User> {
        return this.httpService.get<User>(environment.apiV1BaseUrl + '/accounts/users/me').pipe(
            map((user) => {
                const currentUser = {...user, accessLevel: user.accessLevel};
                return this.cacheService.setItem('currentUser', 0, currentUser)
            })
        );
    }

    fetchAllUsers(userIds?: number[], showApiUsers?: boolean): Observable<Array<User>> {
        let params = new HttpParams();

        if (userIds) {
            userIds.forEach((id: number) => params = params.append('userIds', String(id)))
        }
        if (showApiUsers) {
            params = params.append('showApiUsers', 'true')
        }

        return this.httpService.get<Array<User>>(environment.apiV1BaseUrl + '/accounts/users-list', { params: params }).pipe(
            map(
                (users: Array<User>) => [].concat(users).sort(
                    (a, b) => (a.firstName + a.lastName + a.username).localeCompare(b.firstName + b.lastName + b.username)
                )
            ),
            tap((users: Array<User>) => {
                for (const user of users) {
                    this.cacheService.setItem('users', user.id, user);
                }
            })
        );
    }

    fetchUserOrganizations(): Observable<Array<Organization>> {
        let params = new HttpParams();
        return this.httpService.get<Array<Organization>>(`${environment.ssoApiPath}/user/me/organizations`, { params }).pipe(
            map((res: Array<Organization>) => res)
        );
    }

    getCurrentUser(skipCache?: boolean): Observable<User> {
        if (!skipCache && this.cacheService.hasItem('currentUser', 0)) {
            return of(this.cacheService.getItem('currentUser', 0));
        } else {
            return this.fetchCurrentUser().pipe(
                tap((user: User) => {
                    this.currentUser = user;
                })
            );
        }
    }

    refreshAllUsers(): Observable<Array<User>> {
        return this.fetchAllUsers().pipe(
            tap((users: Array<User>) => {
                this._customerUsers$.next(users);
            })
        );
    }

    get customerUsers$(): Observable<Array<User>> {
        return this._customerUsers$.asObservable();
    }

    createUser(user: UserWriteModel): Observable<number> {

        return this.httpService.post(environment.apiV1BaseUrl + '/accounts/users', user, { observe: 'response' }).pipe(
            map((res: HttpResponse<any>) => {
                if (res.status !== 201) {
                    throw new Error('Create user failed with status: ' + res.status + ' (Expected 201)');
                }
                return HttpService.extractIdFromLocationHeader(res);
            }),
            mergeMap((id) => this.refreshAllUsers().pipe(mapTo(id)))
        );
    }

    updateUser(user: UserUpdateModel): Observable<boolean> {

        if (user.id === this.currentUser.id) {
            return this.updateCurrentUser(user);
        }

        return this.httpService.put(environment.apiV1BaseUrl + '/accounts/users/' + user.id, user, { observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (res.status !== 200) {
                    throw new Error('Update user failed');
                }
            }),
            mergeMap(() => this.refreshAllUsers()),
            mapTo(true)
        );
    }

    updateCurrentUser(user: UserWriteModel): Observable<boolean> {

        return this.httpService.put(environment.apiV1BaseUrl + '/accounts/users/me', user, { observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (res.status !== 200) {
                    throw new Error('Update current user failed');
                }
            }),
            mergeMap(() => this.getCurrentUser(true)),
            mergeMap(() => this.refreshAllUsers()),
            mapTo(true)
        );
    }

    updateUserSetting(userId: number, settingKey: string, setting: UserSetting): Observable<boolean> {

        return this.httpService.put(environment.apiV1BaseUrl + '/accounts/users/' + userId + '/user-settings/' + settingKey, setting,{ observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (res.status !== 200 && res.status !== 201) {
                    throw new Error('Update user settings failed');
                }
            }),
            // Trigger refresh of setting streams and currentUser
            mergeMap(() => this.getCurrentUser(true)),
            mapTo(true)
        );
    }

    setPassword(passwordData: ChangePassword): Observable<number> {

        return this.httpService.post(environment.apiV1BaseUrl + '/accounts/users/me/set-password', passwordData, { observe: 'response' }).pipe(
            map((res: HttpResponse<any>) => {

                if (res.status !== 200) {
                    throw new Error('Setting password failed');
                }

                return parseInt(res.headers.get('Location') || '0', 10);
            }),
            catchError((err: any) => {
                const errorText = JSON.parse(err.response._body).message;
                const apiError = errorText.split(':')[0];
                throw new ControlantAPIError(apiError as ControlantAPIErrorType, errorText);
            })
        );
    }


    resetPassword(userId: number): Observable<boolean> {

        return this.httpService.post(environment.apiV1BaseUrl + '/accounts/users/' + userId + '/reset-password', {}, { observe: 'response' }).pipe(
            map((res: HttpResponse<any>) => {

                if (res.status !== 200) {
                    throw new Error('Setting password failed');
                }

                if (this.cacheService.hasItem('users', userId)) {
                    const user = this.cacheService.getItem<User>('users', userId);
                    user.pwResetActive = true;
                }

                return true;
            })
        );
    }

    getUser(userId: number, skipCache: boolean = false): Observable<User> {
        if (!skipCache && this.cacheService.hasItem('users', userId)) {
            return of(this.cacheService.getItem('users', userId));
        } else {
            return this.fetchUser(userId);
        }
    }

    private fetchUser(userId: number): Observable<User> {
        return this.httpService.get<User>(environment.apiV1BaseUrl + '/accounts/users/' + userId).pipe(
            map((user: User) => this.cacheService.setItem('users', user.id, user))
        );
    }

    reset() {

        if (this.adminMode) {
            this.adminMode = false;
            window.sessionStorage.removeItem('adminMode');
        }

        this.currentUser = null;
    }

}
